import React, { useState, useMemo, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { MemberRegistrationForm, MembershipType, Gender, ShirtSize } from '../../types';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import PersonalDetailsStep from './PersonalDetailsStep';
import MembershipTypeStep from './MembershipTypeStep';
import FamilyMembersStep from './FamilyMembersStep';
import SponsorDetailsStep from './SponsorDetailsStep';
import GuardianDetailsStep from './GuardianDetailsStep';
import CodeOfConductStep from './CodeOfConductStep';
import ReviewStep from './ReviewStep';
import ConfirmationStep from './ConfirmationStep';
import CompEntryStep from './CompEntryStep';
import PaymentChoice from '../payment/PaymentChoice';
import { membershipTypeToFeeKey } from '@shared/fees';
import { deriveLifecycle } from '@shared/competitionRules';
import { registrationAmountCents, registrationAmountDollars } from '../../utils/registrationPrice';

const client = generateClient({ authMode: 'apiKey' });

interface RegistrationWizardProps {
  onComplete?: () => void;
  onCancel?: () => void;
  onNavigateToRules?: () => void;
  onNavigateToConduct?: () => void;
  onNavigateToWaiver?: () => void;
}

type StepId =
  | 'membership'
  | 'sponsor'
  | 'personal'
  | 'guardian'
  | 'family'
  | 'comp'
  | 'conduct'
  | 'review'
  | 'payment';

const RegistrationWizard: React.FC<RegistrationWizardProps> = ({
  onComplete,
  onCancel,
  onNavigateToRules,
  onNavigateToConduct,
  onNavigateToWaiver,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [createdMemberId, setCreatedMemberId] = useState<string | null>(null);
  // Today's open competition (if any) — drives the optional comp-entry step.
  const [compEvent, setCompEvent] = useState<{
    id: string;
    title?: string;
    eventDate?: string;
  } | null>(null);

  const [formData, setFormData] = useState<MemberRegistrationForm>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    age: undefined,
    gender: Gender.PREFER_NOT_TO_SAY,
    shirtSize: ShirtSize.M,
    address: '',
    suburb: '',
    state: '',
    postCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    isSponsor: false,
    businessName: '',
    membershipType: MembershipType.ADULT,
    familyMembers: [],
    amountPaid: 0,
    clubRulesAccepted: false,
    codeOfConductAccepted: false,
    waiverAccepted: false,
  });

  const isSponsorType = (type: MembershipType) =>
    type === MembershipType.SPONSOR_GOLD ||
    type === MembershipType.SPONSOR_SILVER ||
    type === MembershipType.SPONSOR_BRONZE;

  // Build step sequence based on membership type
  const steps: StepId[] = useMemo(() => {
    const type = formData.membershipType;
    const s: StepId[] = ['membership'];

    if (isSponsorType(type)) s.push('sponsor');
    s.push('personal');
    if (type === MembershipType.JUNIOR) s.push('guardian');
    if (type === MembershipType.FAMILY || isSponsorType(type)) s.push('family');
    if (compEvent) s.push('comp');
    s.push('conduct', 'review', 'payment');

    return s;
  }, [formData.membershipType, compEvent]);

  const totalSteps = steps.length;

  const updateFormData = (updates: Partial<MemberRegistrationForm>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Scroll to top when navigating between steps
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStepIndex]);

  // Find today's competition that's open for day-registration. Reuses the
  // same signal the public check-in uses: an active COMPETITION whose
  // sign-on window is currently open (deriveLifecycle(...).signOnOpen). If
  // found, the optional comp-entry step is shown; otherwise it stays hidden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.models.Event.list({
          filter: { eventType: { eq: 'COMPETITION' }, isActive: { eq: true } },
        });
        const now = new Date();
        const open = (data || [])
          .filter((c: any) => deriveLifecycle(c, now).signOnOpen)
          .sort(
            (a: any, b: any) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
          );
        if (!cancelled && open.length > 0) {
          setCompEvent({ id: open[0].id, title: open[0].title, eventDate: open[0].eventDate });
        }
      } catch (err) {
        console.error('Comp-entry availability check failed (step hidden):', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNext = () => setCurrentStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
  const handleBack = () => setCurrentStepIndex((prev) => Math.max(prev - 1, 0));

  // When membership type changes on step 0, reset to step 0
  const handleMembershipNext = () => {
    setCurrentStepIndex(1);
  };

  const handleSubmit = async () => {
    // Honeypot bot detection — real users never fill this hidden field
    if (honeypot) {
      setIsComplete(true);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const isFamily = formData.membershipType === MembershipType.FAMILY;
      // Sponsors get the family step too (sponsorship includes family
      // membership benefits — requirements.md 5.2), so their roster must be
      // submitted as well. The fee is unaffected: the server resolves
      // amountPaid from the sponsor tier, not the family price.
      const carriesFamilyRoster = isFamily || isSponsorType(formData.membershipType);

      // Combined comp registration: only attach intent when a competition is open
      // AND at least one entrant is flagged. The server (buildServerMemberRecord
      // in submit-membership-application/rules.ts) recomputes this same gate from
      // signupCompEventId/signupCompFishing/family fishingToday flags — passing it
      // here just tells the server which competition was open at submission time.
      const anyFishing =
        !!compEvent &&
        (!!formData.signupCompFishing || formData.familyMembers.some((fm: any) => fm.fishingToday));

      // Additive narrow API (see .kiro/specs/BACKLOG.md, 18 Jul 2026 security
      // remediation) — replaces the public generated Member.create. The server
      // allowlists fields and forces status/isActive/paymentReceived/age/isJunior/
      // amountPaid; it also performs its own authoritative duplicate-email check
      // in the same round trip, so the previous separate memberEmailExists
      // pre-check call has been removed as redundant (it added a network round
      // trip without changing the actual outcome — the server always re-validates
      // regardless of what the client checked first).
      const result: any = await (client.mutations as any).submitMembershipApplication({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        shirtSize: formData.shirtSize,
        address: formData.address,
        suburb: formData.suburb || undefined,
        state: formData.state || undefined,
        postCode: formData.postCode || undefined,
        emergencyContactName: formData.emergencyContactName || undefined,
        emergencyContactPhone: formData.emergencyContactPhone || undefined,
        isSponsor: formData.isSponsor,
        businessName: formData.isSponsor ? formData.businessName : undefined,
        sponsorTier: formData.sponsorTier || undefined,
        guardianFirstName: formData.guardianFirstName || undefined,
        guardianLastName: formData.guardianLastName || undefined,
        guardianEmail: formData.guardianEmail || undefined,
        guardianPhone: formData.guardianPhone || undefined,
        membershipType: formData.membershipType,
        // familyMembers is declared as a.json() (AWSJSON) in the schema, which
        // requires a JSON *string* — passing the raw array makes AppSync reject
        // the whole request with "Variable has an invalid value" before the
        // resolver runs, so the Lambda is never invoked and the wizard only
        // ever sees the generic catch-block error. AppSync parses the string
        // back into native JSON for the Lambda, so rules.ts still receives a
        // real array for its Array.isArray()/length validation.
        //
        // Sent for SPONSOR tiers as well as FAMILY: the wizard shows the
        // family step to sponsors (see the steps useMemo above) because a
        // sponsorship includes family membership benefits, per
        // .kiro/specs/archived/membership-registration/requirements.md 5.2.
        // Gating this on FAMILY alone silently discarded everything a sponsor
        // entered on that step.
        familyMembers: carriesFamilyRoster
          ? JSON.stringify(formData.familyMembers)
          : undefined,
        clubRulesAccepted: formData.clubRulesAccepted,
        codeOfConductAccepted: formData.codeOfConductAccepted,
        waiverAccepted: formData.waiverAccepted,
        signupCompEventId: anyFishing ? compEvent!.id : undefined,
        signupCompFishing: anyFishing ? !!formData.signupCompFishing : undefined,
        signupCompEventName: anyFishing ? compEvent!.title : undefined,
        signupCompEventDate: anyFishing ? compEvent!.eventDate : undefined,
      });

      const response = parseLambdaResponse<{
        success?: boolean;
        memberId?: string;
        error?: string;
        message?: string;
      }>(result?.data);

      if (response?.success && response.memberId) {
        // Email is now sent automatically via DynamoDB Stream trigger
        setCreatedMemberId(response.memberId);
        handleNext(); // advance to payment step
      } else {
        // The Lambda's own message is already the correct user-facing copy
        // for every rejection reason (missing fields, agreements, duplicate
        // email, invalid membership type, etc.) — use it directly rather
        // than re-deriving a message from an error-code string match.
        setSubmitError(
          response?.message ||
            'Failed to submit registration. Please try again or contact us for assistance.'
        );
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      if (error.message?.includes('network')) {
        setSubmitError('Network error. Please check your connection and try again.');
      } else {
        setSubmitError(
          'Failed to submit registration. Please try again or contact us for assistance.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    if (isComplete) return <ConfirmationStep formData={formData} />;

    const stepId = steps[currentStepIndex];

    switch (stepId) {
      case 'membership':
        return (
          <MembershipTypeStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleMembershipNext}
            onBack={() => onCancel?.()}
          />
        );
      case 'sponsor':
        return (
          <SponsorDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'personal':
        return (
          <PersonalDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onCancel={handleBack}
          />
        );
      case 'guardian':
        return (
          <GuardianDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'family':
        return (
          <FamilyMembersStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'comp':
        return (
          <CompEntryStep
            formData={formData}
            updateFormData={updateFormData}
            compEvent={compEvent!}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'conduct':
        return (
          <CodeOfConductStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            onNavigateToRules={onNavigateToRules}
            onNavigateToConduct={onNavigateToConduct}
            onNavigateToWaiver={onNavigateToWaiver}
          />
        );
      case 'review':
        return (
          <ReviewStep
            formData={formData}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      case 'payment': {
        const feeKey = membershipTypeToFeeKey(formData.membershipType) ?? 'membership_fee_adult';
        const amountCents = registrationAmountCents(formData);
        const amountDisplay = `$${registrationAmountDollars(formData).toFixed(2)}`;
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Payment</h3>
            {createdMemberId ? (
              <PaymentChoice
                amountCents={amountCents}
                amountDisplay={amountDisplay}
                paymentType="MEMBERSHIP_NEW"
                entityId={createdMemberId}
                entityType="MEMBER"
                payerEmail={formData.email}
                payerName={`${formData.firstName} ${formData.lastName}`}
                feeKey={feeKey}
                authMode="apiKey"
                onSuccess={() => setIsComplete(true)}
                onPayOnDay={() => setIsComplete(true)}
              />
            ) : (
              <p className="text-red-600 text-sm">
                Registration could not be started. Please go back and try again.
              </p>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-6 sm:py-12 px-4">
      {/* Honeypot field — hidden from real users, filled by bots */}
      <input
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: 'absolute', left: '-9999px' }}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <div className="max-w-4xl mx-auto">
        {!isComplete && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Step {currentStepIndex + 1} of {totalSteps}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round(((currentStepIndex + 1) / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-fishing-blue h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentStepIndex + 1) / totalSteps) * 100}%`,
                }}
              />
            </div>
          </div>
        )}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8">{renderStep()}</div>
      </div>
    </div>
  );
};

export default RegistrationWizard;
